import React, { useMemo, useState } from 'react';
import { Row, Col } from 'antd';

import { GovernanceStatusPerDomainWidget } from './GovernanceStatusPerDomainWidget';
import { EntitiesPerGlossaryTermWidget } from './EntitiesPerGlossaryTermWidget';
import TopSelector from '../entity/shared/components/common/TopSelector';
import { HomePageRecommendationV2 } from './HomePageRecommendationV2';
import { DataQualityTrendWidget } from './DataQualityTrendWidget';
import { AssetsPerOwnerWidget } from './AssetsPerOwnerWidget';
import { EntityTilesSection } from './EntityTilesSection';
import { PrivilegeGate } from '../shared/PrivilegeGate';
import { PlatformsWidget } from './PlatformsWidget';
import { DomainsWidget } from './DomainsWidget';

import { buildDashboardFilters, DashboardFiltersProvider, DashboardFilterSelections } from './dashboardFilters';
import { PlatformPrivileges } from '../../types.generated';
import { useTranslation } from '../../i18n/useTranslation';
import { FILTER_SELECTOR_CONFIG } from './utils';

type PrivilegePredicate = (p: PlatformPrivileges | null | undefined) => boolean;

const isDomainRestricted: PrivilegePredicate = (p) => !p?.manageDomains && !p?.viewDomain;

const WIDGET_CONFIG: {
    span: number;
    isRestricted: PrivilegePredicate;
    titleKey: string;
    Widget: React.FC;
}[] = [
    { span: 12, isRestricted: isDomainRestricted, titleKey: 'Domains', Widget: DomainsWidget },
    { span: 12, isRestricted: (p) => !p?.manageDataPlatform, titleKey: 'Platforms', Widget: PlatformsWidget },
    {
        span: 24,
        isRestricted: (p) => !p?.readDataQualityCheckpoint,
        titleKey: 'Data Quality - Checkpoint Score',
        Widget: DataQualityTrendWidget,
    },
    {
        span: 12,
        isRestricted: isDomainRestricted,
        titleKey: 'Governance Status per Domain',
        Widget: GovernanceStatusPerDomainWidget,
    },
    { span: 12, isRestricted: (p) => !p?.manageUsers, titleKey: 'Assets per Owner', Widget: AssetsPerOwnerWidget },
    {
        span: 12,
        isRestricted: (p) => !p?.viewGlossaryTerm && !p?.manageGlossaryTerms,
        titleKey: 'Entities per Glossary Term',
        Widget: EntitiesPerGlossaryTermWidget,
    },
];

/**
 * Root element id used by the PDF export to locate the dashboard, and the attribute that
 * marks each block (widget card / tiles strip) the exporter captures. Kept here so the
 * export utilities and this layout agree on the same contract.
 */
export const DASHBOARD_EXPORT_ROOT_ID = 'dashboard-v2-export-root';
export const DASHBOARD_EXPORT_FILTERS_ATTRIBUTE = 'data-export-filters';

export const DashboardV2 = () => {
    const { t } = useTranslation();

    const [selections, setSelections] = useState<DashboardFilterSelections>({});

    const onSelectField = (field: string) => (urns: string[]) => setSelections((prev) => ({ ...prev, [field]: urns }));

    const dashboardFilters = useMemo(() => buildDashboardFilters(selections), [selections]);

    /**
     * Readable summary of the active filters (e.g. "Domain (2), Owner (1)"), surfaced on the
     * exported PDF header via a data attribute so the export button needn't prop-drill state.
     */
    const filtersSummary = useMemo(
        () =>
            FILTER_SELECTOR_CONFIG.map(({ titleKey, field }) => {
                const count = selections[field]?.length ?? 0;
                return count > 0 ? `${t(titleKey)} (${count})` : null;
            })
                .filter(Boolean)
                .join(', '),
        [selections, t],
    );

    return (
        <DashboardFiltersProvider value={dashboardFilters}>
            <div
                className="f-custom-layout"
                id={DASHBOARD_EXPORT_ROOT_ID}
                {...{ [DASHBOARD_EXPORT_FILTERS_ATTRIBUTE]: filtersSummary }}
            >
                <Row align="middle" gutter={[8, 8]}>
                    {FILTER_SELECTOR_CONFIG.map(({ titleKey, entityType, field, filter }) => (
                        <Col key={field} flex="none">
                            <TopSelector
                                title={t(titleKey)}
                                entityType={entityType}
                                filter={filter}
                                onChange={onSelectField(field)}
                            />
                        </Col>
                    ))}
                </Row>
                <div data-export-section>
                    <EntityTilesSection />
                </div>
                <Row gutter={[16, 16]}>
                    {WIDGET_CONFIG.map(({ span, isRestricted, titleKey, Widget }) => (
                        <Col key={titleKey} lg={span} xs={24} data-export-section>
                            <PrivilegeGate isRestricted={isRestricted} title={t(titleKey)}>
                                <Widget />
                            </PrivilegeGate>
                        </Col>
                    ))}
                    <HomePageRecommendationV2 />
                </Row>
            </div>
        </DashboardFiltersProvider>
    );
};
